import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { addDays, startOfWeek, format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { usePostWeekMenu } from "./usePostWeekMenu";
import ButtonIcon from "../../interface/ButtonIcon";
import { IoChevronBackCircle, IoChevronForwardCircle } from "react-icons/io5";
import { useGetLunchMealsShortList } from "./useGetLunchMealsShortList";
import { useGetWeekMenu } from "./useGetWeekMenu";
import Fuse from "fuse.js";
import FormRow from "../../interface/FormRow";
import {
  fetchLunchSnapshotById,
  useGetLunchSnapshotById,
} from "./useGetLunchSnapshotById";

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-left: 4rem;
  padding-right: 1rem;
  width: 100%;
  height: 100%;
  min-height: 50rem;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 9rem;
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: 80% 20%;
  gap: 2rem;
  margin-bottom: 1.2rem;
`;

const SnapShotContainer = styled.img`
  height: 100px;
  width: 100px;
`;

const StyledHeader = styled.header`
  display: flex;
  position: fixed;
  top: 1rem;
  left: 0;
  z-index: 90;
  background-color: var(--color-grey-0);
  padding: 0.8rem 2.8rem;
  margin: 0.4rem 0.8rem;
  width: calc(100% - 1.6rem);
  border-radius: 2rem;
  box-shadow: var(--shadow-main);
  align-items: start;
`;

const StyledAppName = styled.h1`
  font-size: 1.2em;
  color: var(--color-witron-blue);
  margin-left: 2em;
  margin-right: 3em;
  align-self: center;
`;

const StyledDateRange = styled.h2`
  font-size: 1.1em;
  color: var(--color-grey-700);
  align-self: center;
`;

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function WeekMenuEdit() {
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [weekData, setWeekData] = useState(() =>
    daysOfWeek.reduce((acc, day) => {
      acc[day] = { meal: null, event: "" };
      return acc;
    }, {})
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const { mutate: submitWeekMenu } = usePostWeekMenu();
  const { data: mealListData } = useGetLunchMealsShortList();
  const { data: storedWeekMenu } = useGetWeekMenu(
    format(currentWeekStart, "yyyy-MM-dd")
  );

  const [snapshots, setSnapshots] = useState({});

  // Load snapshots for the week's menu meals
  useEffect(() => {
    const fetchSnapshots = async () => {
      if (!storedWeekMenu?.days?.length) return;

      const uniqueMealIds = [
        ...new Set(
          storedWeekMenu.days.map((d) => d.mealId).filter((id) => !!id)
        ),
      ];

      const snapshotMap = {};
      for (const id of uniqueMealIds) {
        try {
          const res = await fetchLunchSnapshotById(id); // You can extract this from your hook or service
          snapshotMap[id] = res ?? null;
        } catch (e) {
          snapshotMap[id] = null; // fallback if error
        }
      }

      setSnapshots(snapshotMap);
    };

    fetchSnapshots();
  }, [storedWeekMenu]);

  useEffect(() => {
    if (storedWeekMenu?.days?.length) {
      const updated = {};
      storedWeekMenu.days.forEach((day) => {
        const weekday = format(new Date(day.date), "EEEE");
        updated[weekday] = {
          meal: day.mealId ? { value: day.mealId, label: day.mealName } : null,
          event: day.specialEvent || "",
        };
      });
      setWeekData((prev) => ({ ...prev, ...updated }));
    }
  }, [storedWeekMenu]);

  const normalize = (text) =>
    text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const fuse = useMemo(() => {
    return new Fuse(mealListData || [], {
      keys: ["menuItemName"],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
      matchAllTokens: true,
    });
  }, [mealListData]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return (
        mealListData?.map((meal) => ({
          value: meal.id,
          label: meal.menuItemName,
        })) ?? []
      );
    }

    const normalizedInput = normalize(searchTerm);
    const results = fuse.search(normalizedInput);

    return results.map((res) => ({
      value: res.item.id,
      label: res.item.menuItemName,
    }));
  }, [searchTerm, fuse, mealListData]);

  const handleMealChange = async (day, selected) => {
    setWeekData((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        meal: selected,
      },
    }));

    if (selected?.value) {
      // Update mealId in query params
      setSearchParams({ mealId: selected.value });

      // If snapshot is missing, fetch and add it
      if (!snapshots[selected.value]) {
        try {
          const snapshot = await fetchLunchSnapshotById(selected.value);
          setSnapshots((prev) => ({
            ...prev,
            [selected.value]: snapshot ?? null,
          }));
        } catch (e) {
          console.warn("Snapshot fetch failed:", e);
          setSnapshots((prev) => ({
            ...prev,
            [selected.value]: null,
          }));
        }
      }
    } else {
      setSearchParams({});
    }
  };

  const handleEventChange = (day, eventText) => {
    setWeekData((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        event: eventText,
      },
    }));
  };

  const onSubmit = () => {
    const days = daysOfWeek.map((day, i) => ({
      date: format(addDays(currentWeekStart, i), "yyyy-MM-dd"),
      mealId: weekData[day].meal?.value ?? null,
      specialEvent: weekData[day].event || "",
    }));

    submitWeekMenu({
      days,
      modifiedBy: "frontend-user",
    });
  };

  const adjustWeek = (days) => {
    setCurrentWeekStart((prev) => addDays(prev, days));
    setWeekData(() =>
      daysOfWeek.reduce((acc, day) => {
        acc[day] = { meal: null, event: "" };
        return acc;
      }, {})
    );
  };

  const startDate = format(currentWeekStart, "MMM d");
  const endDate = format(addDays(currentWeekStart, 6), "MMM d");

  return (
    <TopContainer>
      <StyledHeader>
        <StyledAppName>Week Menu Edit</StyledAppName>
        <ButtonIcon onClick={() => adjustWeek(-7)}>
          <IoChevronBackCircle />
        </ButtonIcon>
        <StyledDateRange>
          {startDate} - {endDate}
        </StyledDateRange>
        <ButtonIcon onClick={() => adjustWeek(7)}>
          <IoChevronForwardCircle />
        </ButtonIcon>
        <ButtonIcon onClick={onSubmit}>Submit</ButtonIcon>
      </StyledHeader>

      <FormContainer>
        {daysOfWeek.map((day) => (
          <RowContainer
            key={day}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const mealId = e.dataTransfer.getData("mealId");
              const meal = mealListData?.find((m) => m.id === mealId);
              if (meal) {
                const selectedOption = {
                  value: mealId,
                  label: meal.menuItemName,
                };
                handleMealChange(day, selectedOption);
              }
            }}
          >
            <FormRow label={day}>
              <Select
                value={weekData[day].meal}
                onInputChange={(value) => setSearchTerm(value)}
                onChange={(option) => handleMealChange(day, option)}
                onKeyDown={(e) => {
                  if (e.key === "Delete" || e.key === "Backspace") {
                    handleMealChange(day, null);
                  }
                }}
                options={filteredOptions}
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: "26rem",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menu: (base) => ({
                    ...base,
                    minWidth: "26rem",
                  }),
                }}
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 4,
                  colors: {
                    ...theme.colors,
                    primary25: "#2c2f33",
                    primary: "#7289da",
                    neutral0: "#23272a",
                    neutral80: "#ffffff",
                  },
                })}
              />
            </FormRow>
            <SnapShotContainer
              src={
                weekData[day].meal?.value
                  ? snapshots[weekData[day].meal.value] ||
                    "/images/NoMealImage.png"
                  : "/images/NoMealImage.png"
              }
              alt="Meal Snapshot"
            />
          </RowContainer>
        ))}
      </FormContainer>
    </TopContainer>
  );
}

export default WeekMenuEdit;
